import {
  AutomationContextAttributeType,
  AutomationFailureResolutionType,
  AutomationFailureResponseArea,
  AutomationFailureResponseReason,
  ResolveAutomationFailureRequestResolution,
  type AutomationContextAttribute,
  type AutomationFailureResponse,
} from '@urbanx/agx.support.client';
import { formatAutomationFailureInstant } from './automationFailureDateTime';

const areaLabels: Record<AutomationFailureResponseArea, string> = {
  [AutomationFailureResponseArea.Email]: 'Email',
  [AutomationFailureResponseArea.Crm]: 'CRM',
  [AutomationFailureResponseArea.Tasks]: 'Tasks',
  [AutomationFailureResponseArea.Documents]: 'Documents',
  [AutomationFailureResponseArea.DocuSign]: 'DocuSign',
  [AutomationFailureResponseArea.CampaignFlow]: 'Campaign Flow',
  [AutomationFailureResponseArea.TitleSearch]: 'Title Search',
  [AutomationFailureResponseArea.IdentityVerification]: 'Identity verification',
  [AutomationFailureResponseArea.Marketing]: 'Marketing',
};

const reasonLabels: Record<AutomationFailureResponseReason, string> = {
  [AutomationFailureResponseReason.InvalidData]: 'Invalid data',
  [AutomationFailureResponseReason.ProviderUnavailable]: 'Provider unavailable',
  [AutomationFailureResponseReason.PermissionDenied]: 'Permission denied',
  [AutomationFailureResponseReason.RecordNotFound]: 'Record not found',
  [AutomationFailureResponseReason.TimedOut]: 'Timed out',
  [AutomationFailureResponseReason.Unexpected]: 'Unexpected',
};

const reasonExplanations: Record<AutomationFailureResponseReason, string> = {
  [AutomationFailureResponseReason.InvalidData]:
    'The information this automation needed was missing or invalid, so it could not finish.',
  [AutomationFailureResponseReason.ProviderUnavailable]:
    'An external provider was unavailable, so the automation could not finish.',
  [AutomationFailureResponseReason.PermissionDenied]:
    'Autopilot was not allowed to complete this action. The connected account may have lost access.',
  [AutomationFailureResponseReason.RecordNotFound]:
    'A record this automation depends on could not be found.',
  [AutomationFailureResponseReason.TimedOut]:
    'The automation did not receive a response in time and stopped waiting.',
  [AutomationFailureResponseReason.Unexpected]:
    'Something unexpected went wrong. Copy the diagnostics below for a developer to investigate.',
};

export const getAutomationAreaLabel = (area?: AutomationFailureResponseArea) =>
  area ? areaLabels[area] : 'Unknown area';

export const getAutomationReasonLabel = (reason?: AutomationFailureResponseReason) =>
  reason ? reasonLabels[reason] : 'Unknown reason';

export const getAutomationReasonExplanation = (reason?: AutomationFailureResponseReason) =>
  reason ? reasonExplanations[reason] : 'No explanation was recorded for this automation failure.';

export const getAutomationResolutionLabel = (
  resolution?: AutomationFailureResolutionType | ResolveAutomationFailureRequestResolution
) => {
  switch (resolution) {
    case AutomationFailureResolutionType.ManuallyActioned:
      return 'Manually actioned';
    case AutomationFailureResolutionType.Ignored:
      return 'Ignored';
    case AutomationFailureResolutionType.FalseAlarm:
      return 'False alarm';
    default:
      return 'Unknown action';
  }
};

const splitIdentifier = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/\bId\b/g, 'ID')
    .replace(/\bUtc\b/g, 'UTC');

export const formatAutomationContextName = (name?: string) => {
  if (!name) return 'Detail';

  const label = splitIdentifier(name);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const formatAutomationContextValue = (attribute: AutomationContextAttribute) => {
  if (attribute.value == null) return 'Not recorded';

  switch (attribute.type) {
    case AutomationContextAttributeType.Boolean:
      return attribute.value === 'true' ? 'Yes' : 'No';
    case AutomationContextAttributeType.Enum:
      return formatAutomationContextName(attribute.value);
    case AutomationContextAttributeType.Instant:
      return formatAutomationFailureInstant(attribute.value);
    default:
      return attribute.value;
  }
};

const diagnosticLine = (label: string, value?: string) =>
  value ? `${label}: ${value}` : undefined;

const getExceptionLabel = (index: number, exceptionCount: number) =>
  exceptionCount === 1 ? 'Exception' : `Exception ${index + 1}`;

type AutomationFailureDiagnosticEntry = [label: string, value: string];

const diagnosticEntry = (
  label: string,
  value?: string
): AutomationFailureDiagnosticEntry | undefined => (value ? [label, value] : undefined);

/** Builds the compact developer-reference rows shown in the failure detail. */
export const getAutomationFailureDiagnosticEntries = (
  failure: AutomationFailureResponse
): AutomationFailureDiagnosticEntry[] => {
  const references = [
    diagnosticEntry(
      'Reason',
      failure.reason ? getAutomationReasonLabel(failure.reason) : undefined
    ),
    diagnosticEntry('Service', failure.diagnostics?.service),
    diagnosticEntry('Function', failure.diagnostics?.function),
    diagnosticEntry('Event', failure.diagnostics?.event),
    diagnosticEntry('Queue', failure.diagnostics?.queue),
    diagnosticEntry('Session ID', failure.diagnostics?.sessionId),
    diagnosticEntry('Invocation ID', failure.diagnostics?.invocationId),
    diagnosticEntry('Trace ID', failure.diagnostics?.traceId),
  ].filter((entry): entry is AutomationFailureDiagnosticEntry => entry !== undefined);

  const exceptions = failure.exceptions ?? [];
  const exceptionEntries = exceptions.flatMap<AutomationFailureDiagnosticEntry>(
    (exception, index) => [
      [getExceptionLabel(index, exceptions.length), exception.type ?? 'Not recorded'],
      ['Message', exception.message ?? 'Not recorded'],
      ['Stack trace', exception.stackTrace ?? 'Not recorded'],
    ]
  );

  return [...references, ...exceptionEntries];
};

const diagnosticAttributes = (heading: string, attributes?: AutomationContextAttribute[]) => {
  if (!attributes?.length) return [];

  return [
    '',
    heading,
    ...attributes.map(
      (attribute) =>
        `${attribute.name ?? 'Detail'}: ${attribute.value ?? ''}${attribute.type ? ` (${attribute.type})` : ''}`
    ),
  ];
};

/** Builds the complete technical payload shown and copied from the diagnostics section. */
export const buildAutomationFailureDiagnostics = (failure: AutomationFailureResponse) => {
  const references = [
    diagnosticLine('Failure reference', failure.reference),
    diagnosticLine('Agency ID', failure.agencyId),
    diagnosticLine('Campaign ID', failure.campaignId),
    diagnosticLine('Contract ID', failure.contractId),
    diagnosticLine('Task ID', failure.taskId),
    diagnosticLine('Service', failure.diagnostics?.service),
    diagnosticLine('Function', failure.diagnostics?.function),
    diagnosticLine('Event', failure.diagnostics?.event),
    diagnosticLine('Queue', failure.diagnostics?.queue),
    diagnosticLine('Session ID', failure.diagnostics?.sessionId),
    diagnosticLine('Invocation ID', failure.diagnostics?.invocationId),
    diagnosticLine('Trace ID', failure.diagnostics?.traceId),
  ].filter((line): line is string => line !== undefined);

  const exceptionCount = failure.exceptions?.length ?? 0;
  const exceptions = (failure.exceptions ?? []).flatMap((exception, index) => [
    '',
    getExceptionLabel(index, exceptionCount),
    diagnosticLine('Type', exception.type) ?? 'Type: Not recorded',
    diagnosticLine('Message', exception.message) ?? 'Message: Not recorded',
    diagnosticLine('Stack trace', exception.stackTrace) ?? 'Stack trace: Not recorded',
  ]);

  const history = (failure.resolutionHistory ?? []).flatMap((resolution, index) => [
    '',
    `Resolution ${index + 1}`,
    diagnosticLine('Action', resolution.type),
    diagnosticLine('Staff user ID', resolution.staffUserId),
    diagnosticLine('Resolved UTC', resolution.resolvedUtc),
    diagnosticLine('Reopened by staff user ID', resolution.reopenedByStaffUserId),
    diagnosticLine('Reopened UTC', resolution.reopenedUtc),
  ]);

  return [
    `${failure.automationName ?? 'Automation'} failed`,
    diagnosticLine('Area', failure.area),
    diagnosticLine('Reason', failure.reason),
    diagnosticLine('Detail', failure.detail),
    diagnosticLine('Status', failure.status),
    diagnosticLine('Occurred UTC', failure.occurredUtc),
    ...references,
    ...diagnosticAttributes('Attributes', failure.attributes),
    ...diagnosticAttributes('Tags', failure.tags),
    ...exceptions,
    ...history,
  ]
    .filter((line): line is string => line !== undefined)
    .join('\n');
};

export {
  formatAutomationFailureInstant,
  formatAutomationFailureRelativeInstant,
  formatAutomationFailureShortInstant,
} from './automationFailureDateTime';
